import React, { useEffect, useState, useMemo } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Select, Textarea } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { FilterBar } from '@/components/ui/FilterBar';
import { api } from '@/api/client';
import { useAuth } from '@/context/AuthContext';
import { useCurrency, formatCurrency } from '@/hooks/useCurrency';
import { today } from '@/utils/helpers';
import { exportToCSV, exportToExcel, exportPLToExcel, exportPLToPDF } from '@/utils/exportUtils';
import { Plus, Edit, Trash, Check, X, Receipt, Plane, Utensils, Download, FileSpreadsheet, FileText } from 'lucide-react';

const PAYMENT_METHODS = ['Cash', 'Bank Transfer', 'Card', 'Other'];
const STATUSES = ['Pending', 'Approved', 'Rejected'];

const emptyForm = () => ({
  category: '',
  description: '',
  amount: 0,
  date: today(),
  payment_method: 'Cash',
  notes: '',
  travel: { destination: '', purpose: '', distance: 0, fuel_cost: 0, toll_charges: 0, parking_charges: 0 },
  food: { staff_name: '', people_count: 0, location: '', reason: '' },
  linked_rma_id: ''
});

export const Expenses = () => {
  const currency = useCurrency();
  const { isAdmin, isSuperAdmin, user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showTravel, setShowTravel] = useState(false);
  const [showFood, setShowFood] = useState(false);
  const [pl, setPl] = useState(null);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  const isManager = user?.role === 'manager' || isAdmin();

  useEffect(() => { loadAll(); }, [dateFrom, dateTo]);

  const loadAll = async () => {
    try {
      const [eRes, cRes, plRes] = await Promise.all([
        api.getExpenses({ start_date: dateFrom, end_date: dateTo }),
        api.getExpenseCategories(),
        api.getProfitLoss({ start_date: dateFrom, end_date: dateTo }).catch(() => ({ data: null }))
      ]);
      setExpenses(eRes.data);
      setCategories(cRes.data.categories || []);
      setPl(plRes.data);
    } catch (e) { console.error(e); }
  };

  const handleNew = () => {
    setEditing(null);
    setFormData({ ...emptyForm(), category: categories[0] || '' });
    setShowTravel(false); setShowFood(false);
    setShowForm(true);
  };

  const handleEdit = (e) => {
    setEditing(e);
    setFormData({
      category: e.category,
      description: e.description,
      amount: e.amount,
      date: e.date,
      payment_method: e.payment_method || 'Cash',
      notes: e.notes || '',
      travel: e.travel || emptyForm().travel,
      food: e.food || emptyForm().food,
      linked_rma_id: e.linked_rma_id || ''
    });
    setShowTravel(!!(e.travel?.destination || e.travel?.fuel_cost));
    setShowFood(!!(e.food?.staff_name || e.food?.reason));
    setShowForm(true);
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!formData.category || !formData.description || !formData.amount) {
      alert('Category, description, and amount are required'); return;
    }
    setLoading(true);
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        travel: showTravel ? formData.travel : null,
        food: showFood ? formData.food : null
      };
      if (editing) await api.updateExpense(editing.id, payload);
      else await api.createExpense(payload);
      setShowForm(false);
      loadAll();
    } catch (e) {
      alert('Failed to save'); console.error(e);
    } finally { setLoading(false); }
  };

  const handleApprove = async (e, status) => {
    if (!isManager) return;
    try {
      await api.approveExpense(e.id, status);
      loadAll();
    } catch { alert('Approval failed'); }
  };

  const handleDelete = async (e) => {
    if (!window.confirm(`Delete ${e.exp_number}?`)) return;
    try { await api.deleteExpense(e.id); loadAll(); } catch { alert('Failed'); }
  };

  const handleExport = () => {
    exportToCSV(filtered, 'expenses', [
      { key: 'exp_number', label: 'Expense #' },
      { key: 'date', label: 'Date' },
      { key: 'category', label: 'Category' },
      { key: 'description', label: 'Description' },
      { key: 'amount', label: 'Amount' },
      { key: 'payment_method', label: 'Payment' },
      { key: 'status', label: 'Status' },
      { key: 'created_by_name', label: 'Entered By' }
    ]);
  };

  const expenseColumns = [
    { key: 'exp_number', label: 'Expense #' },
    { key: 'date', label: 'Date' },
    { key: 'category', label: 'Category' },
    { key: 'description', label: 'Description' },
    { key: 'amount', label: 'Amount' },
    { key: 'payment_method', label: 'Payment' },
    { key: 'status', label: 'Status' },
    { key: 'linked_rma_id', label: 'Linked RMA' },
    { key: 'created_by_name', label: 'Entered By' },
    { key: 'notes', label: 'Notes' }
  ];

  const handleExportExcel = () => exportToExcel(filtered, 'expenses', expenseColumns, 'Expenses');
  const handleExportPLExcel = () => exportPLToExcel(pl, filtered, 'profit_loss');
  const handleExportPLPDF = () => exportPLToPDF(pl, filtered, currency);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return expenses.filter(e => {
      if (filterCategory && e.category !== filterCategory) return false;
      if (filterStatus && e.status !== filterStatus) return false;
      if (!q) return true;
      return (e.exp_number || '').toLowerCase().includes(q)
        || e.description.toLowerCase().includes(q)
        || (e.notes || '').toLowerCase().includes(q)
        || (e.created_by_name || '').toLowerCase().includes(q);
    });
  }, [expenses, search, filterCategory, filterStatus]);

  const totalShown = filtered.reduce((s, e) => s + (e.amount || 0), 0);

  const handleAddCategory = async () => {
    const name = window.prompt('New category name:');
    if (!name) return;
    try {
      await api.addExpenseCategory(name);
      const c = await api.getExpenseCategories();
      setCategories(c.data.categories || []);
      setFormData({ ...formData, category: name });
    } catch { alert('Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-medium tracking-tight text-[var(--text-primary)]">Expenses</h1>
        <Button onClick={handleNew} data-testid="new-expense-button"><Plus size={18} className="mr-2" /> New Expense</Button>
      </div>

      {/* P&L Summary */}
      {pl && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <p className="text-xs uppercase text-[var(--text-muted)]">Revenue</p>
            <p className="text-2xl font-semibold mt-1 text-emerald-600 dark:text-emerald-400" data-testid="pl-revenue">{formatCurrency(pl.revenue, currency)}</p>
            <p className="text-[10px] text-[var(--text-muted)] mt-1">{pl.invoice_count} completed invoice{pl.invoice_count !== 1 ? 's' : ''}</p>
          </Card>
          <Card>
            <p className="text-xs uppercase text-[var(--text-muted)]">Total Expenses</p>
            <p className="text-2xl font-semibold mt-1 text-red-600 dark:text-red-400" data-testid="pl-expenses">{formatCurrency(pl.total_expenses, currency)}</p>
            <p className="text-[10px] text-[var(--text-muted)] mt-1">approved only</p>
          </Card>
          <Card>
            <p className="text-xs uppercase text-[var(--text-muted)]">Net Profit</p>
            <p className={`text-2xl font-semibold mt-1 ${pl.net_profit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`} data-testid="pl-net">
              {formatCurrency(pl.net_profit, currency)}
            </p>
          </Card>
          <Card>
            <p className="text-xs uppercase text-[var(--text-muted)]">Outstanding</p>
            <p className="text-base font-semibold mt-1 text-amber-600 dark:text-amber-400">Customer: {formatCurrency(pl.outstanding_customer_payments, currency)}</p>
            <p className="text-xs text-[var(--text-muted)]">Supplier: {formatCurrency(pl.outstanding_supplier_credits, currency)}</p>
          </Card>
        </div>
      )}

      {/* Date Range */}
      <Card>
        <div className="flex items-end gap-3 flex-wrap">
          <div><label className="text-xs text-[var(--text-muted)] mb-1 block">From</label><Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="exp-from" /></div>
          <div><label className="text-xs text-[var(--text-muted)] mb-1 block">To</label><Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="exp-to" /></div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-[var(--text-muted)]">Expense list</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleExport} disabled={!filtered.length} data-testid="export-exp-csv"><Download size={14} className="mr-1" /> CSV</Button>
              <Button size="sm" variant="outline" onClick={handleExportExcel} disabled={!filtered.length} data-testid="export-exp-xlsx"><FileSpreadsheet size={14} className="mr-1" /> Excel</Button>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-[var(--text-muted)]">P&amp;L report</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleExportPLExcel} disabled={!pl} data-testid="export-pl-xlsx"><FileSpreadsheet size={14} className="mr-1" /> P&amp;L Excel</Button>
              <Button size="sm" variant="outline" onClick={handleExportPLPDF} disabled={!pl} data-testid="export-pl-pdf"><FileText size={14} className="mr-1" /> P&amp;L PDF</Button>
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <FilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search EXP#, description, notes, staff..."
          filters={[
            { key: 'cat', label: 'All Categories', value: filterCategory, options: categories, onChange: setFilterCategory },
            { key: 'status', label: 'All Statuses', value: filterStatus, options: STATUSES, onChange: setFilterStatus }
          ]}
          onClear={() => { setSearch(''); setFilterCategory(''); setFilterStatus(''); }}
          testId="expenses-filter"
        />
        <p className="text-xs text-[var(--text-muted)] mt-2 mb-3">
          Showing {filtered.length} expense{filtered.length !== 1 ? 's' : ''} totalling <strong>{formatCurrency(totalShown, currency)}</strong>
        </p>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>EXP #</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Payment</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Entered By</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-[var(--text-muted)]">No expenses match filters.</TableCell></TableRow>
            ) : filtered.map(e => (
              <TableRow key={e.id}>
                <TableCell className="font-mono text-xs font-semibold">{e.exp_number}</TableCell>
                <TableCell>{e.date}</TableCell>
                <TableCell className="text-xs">
                  {e.category}
                  {e.travel?.destination && <Plane size={10} className="inline ml-1 text-blue-500" />}
                  {e.food?.reason && <Utensils size={10} className="inline ml-1 text-orange-500" />}
                </TableCell>
                <TableCell className="max-w-xs truncate">{e.description}</TableCell>
                <TableCell className="text-right font-semibold">{formatCurrency(e.amount, currency)}</TableCell>
                <TableCell className="text-xs">{e.payment_method}</TableCell>
                <TableCell>
                  <Badge variant={e.status === 'Approved' ? 'success' : e.status === 'Rejected' ? 'danger' : 'warning'}>{e.status}</Badge>
                </TableCell>
                <TableCell className="text-xs text-[var(--text-muted)]">{e.created_by_name || '—'}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {isManager && e.status === 'Pending' && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => handleApprove(e, 'Approved')} title="Approve" data-testid={`approve-exp-${e.id}`}><Check size={14} className="text-emerald-600" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => handleApprove(e, 'Rejected')} title="Reject" data-testid={`reject-exp-${e.id}`}><X size={14} className="text-red-600" /></Button>
                      </>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => handleEdit(e)} data-testid={`edit-exp-${e.id}`}><Edit size={14} /></Button>
                    {isSuperAdmin() && <Button size="sm" variant="ghost" onClick={() => handleDelete(e)} data-testid={`delete-exp-${e.id}`}><Trash size={14} className="text-red-600" /></Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editing ? `Edit ${editing.exp_number}` : 'New Expense'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex gap-2 items-end">
              <Select label="Category *" value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })} data-testid="exp-category">
                <option value="">-- Select --</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </Select>
              {isAdmin() && <Button type="button" size="sm" variant="outline" onClick={handleAddCategory} title="Add category">+</Button>}
            </div>
            <Input label="Date *" type="date" value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} data-testid="exp-date" />
          </div>
          <Input label="Description *" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} data-testid="exp-description" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Amount *" type="number" step="0.01" min="0" value={formData.amount} onChange={e => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })} data-testid="exp-amount" />
            <Select label="Payment Method" value={formData.payment_method} onChange={e => setFormData({ ...formData, payment_method: e.target.value })} data-testid="exp-payment">
              {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
            </Select>
          </div>

          {/* Travel expansion */}
          <div className="border border-blue-200 dark:border-blue-800 rounded-md">
            <button type="button" onClick={() => setShowTravel(!showTravel)} className="w-full px-3 py-2 flex items-center justify-between text-sm text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20" data-testid="toggle-travel">
              <span className="flex items-center gap-2"><Plane size={14} /> Travel details (optional)</span>
              <span>{showTravel ? '−' : '+'}</span>
            </button>
            {showTravel && (
              <div className="p-3 grid grid-cols-2 gap-3 border-t border-blue-200 dark:border-blue-800">
                <Input label="Destination" value={formData.travel.destination} onChange={e => setFormData({ ...formData, travel: { ...formData.travel, destination: e.target.value } })} data-testid="exp-travel-dest" />
                <Input label="Purpose" value={formData.travel.purpose} onChange={e => setFormData({ ...formData, travel: { ...formData.travel, purpose: e.target.value } })} />
                <Input label="Distance (km)" type="number" value={formData.travel.distance} onChange={e => setFormData({ ...formData, travel: { ...formData.travel, distance: parseFloat(e.target.value) || 0 } })} />
                <Input label="Fuel Cost" type="number" value={formData.travel.fuel_cost} onChange={e => setFormData({ ...formData, travel: { ...formData.travel, fuel_cost: parseFloat(e.target.value) || 0 } })} />
                <Input label="Toll Charges" type="number" value={formData.travel.toll_charges} onChange={e => setFormData({ ...formData, travel: { ...formData.travel, toll_charges: parseFloat(e.target.value) || 0 } })} />
                <Input label="Parking Charges" type="number" value={formData.travel.parking_charges} onChange={e => setFormData({ ...formData, travel: { ...formData.travel, parking_charges: parseFloat(e.target.value) || 0 } })} />
              </div>
            )}
          </div>

          {/* Food expansion */}
          <div className="border border-orange-200 dark:border-orange-800 rounded-md">
            <button type="button" onClick={() => setShowFood(!showFood)} className="w-full px-3 py-2 flex items-center justify-between text-sm text-orange-700 dark:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-900/20" data-testid="toggle-food">
              <span className="flex items-center gap-2"><Utensils size={14} /> Food / meal details (optional)</span>
              <span>{showFood ? '−' : '+'}</span>
            </button>
            {showFood && (
              <div className="p-3 grid grid-cols-2 gap-3 border-t border-orange-200 dark:border-orange-800">
                <Input label="Staff Name" value={formData.food.staff_name} onChange={e => setFormData({ ...formData, food: { ...formData.food, staff_name: e.target.value } })} />
                <Input label="# of People" type="number" value={formData.food.people_count} onChange={e => setFormData({ ...formData, food: { ...formData.food, people_count: parseInt(e.target.value) || 0 } })} />
                <Input label="Location" value={formData.food.location} onChange={e => setFormData({ ...formData, food: { ...formData.food, location: e.target.value } })} />
                <Input label="Reason" value={formData.food.reason} onChange={e => setFormData({ ...formData, food: { ...formData.food, reason: e.target.value } })} placeholder="e.g. Supplier meeting lunch" />
              </div>
            )}
          </div>

          <Textarea label="Notes" value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} rows={2} data-testid="exp-notes" />

          <div className="flex justify-end gap-2 pt-3 border-t border-[var(--border)]">
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={loading} data-testid="save-exp">{loading ? 'Saving...' : (editing ? 'Update' : 'Create')}</Button>
          </div>
          {!editing && (
            <p className="text-[11px] text-[var(--text-muted)] text-center">New expenses start as <strong>Pending</strong>. Manager/Admin can approve from the list.</p>
          )}
        </form>
      </Modal>
    </div>
  );
};
